import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { Panel, RiskBadge, StatusChip } from "@/lovable-demo/components/bits";
import { cn } from "@/lib/utils";
import { recordsForProgram } from "@/lovable-demo/lib/warranty/aggregate";
import { currentStage, fmtCompactCurrency } from "@/lovable-demo/lib/warranty/calc";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

export const Route = createFileRoute("/demo/closed-loop")({
  head: () => ({
    meta: [
      { title: "Broadview Closed Loop — One Governed Problem Record" },
      {
        name: "description",
        content:
          "The Broadview closed-loop application: one governed Problem Record connecting signal, evidence, action, financial consequence and verified outcome.",
      },
      { property: "og:title", content: "Broadview Closed Loop — One Governed Problem Record" },
      {
        property: "og:description",
        content:
          "The Broadview closed-loop application: one governed Problem Record connecting signal, evidence, action, financial consequence and verified outcome.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ClosedLoop,
});

type Loop = {
  id: number;
  name: string;
  question: string;
  detail: string;
  /** operational workflow stage indexes (1-based, of 11) */
  stages: number[];
  tone: "primary" | "accent" | "success" | "warning";
};

const LOOP: Loop[] = [
  {
    id: 1,
    name: "Connected Signal",
    question: "What is changing?",
    detail:
      "Warranty claims, telematics and dealer comments are fused into one aggregate signal with statistical confirmation.",
    stages: [1],
    tone: "primary",
  },
  {
    id: 2,
    name: "Management Top 5",
    question: "What matters now?",
    detail:
      "Signals are ranked by risk and financial exposure and accepted into governance review as the management Top 5.",
    stages: [2],
    tone: "primary",
  },
  {
    id: 3,
    name: "Connected Evidence",
    question: "Why is it happening?",
    detail:
      "Field, claim-level, test and supplier evidence is attached before the record can advance — no evidence, no stage completion.",
    stages: [3],
    tone: "primary",
  },
  {
    id: 4,
    name: "Broadview Problem Record",
    question: "The governed digital thread",
    detail:
      "One record holds problem definition, containment, root cause, corrective action, engineering change, breakpoint, PFMEA / control plan and effectiveness as chapters, with owner, champion and clause mapping.",
    stages: [4, 5, 6, 7, 8, 9],
    tone: "warning",
  },
  {
    id: 5,
    name: "Prevention / Read-Across",
    question: "Where else can it happen?",
    detail:
      "Similar platforms and shared components are screened, prevention actions assigned and tracked to clearance.",
    stages: [11],
    tone: "primary",
  },
  {
    id: 6,
    name: "Broadview Recommendation",
    question: "What should management do?",
    detail:
      "The system proposes the governed next action with the evidence and cost basis behind it.",
    stages: [10],
    tone: "accent",
  },
  {
    id: 7,
    name: "Financial Intelligence",
    question: "What is action versus delay costing?",
    detail:
      "Actual cost, exposure, remaining exposure and delay cost quantify the price of waiting another month.",
    stages: [10],
    tone: "warning",
  },
  {
    id: 8,
    name: "Human Decision + Action",
    question: "Accept, modify or reject",
    detail:
      "A named decision-maker accepts, modifies or rejects the recommendation; the choice is written to the audit trail.",
    stages: [8],
    tone: "accent",
  },
  {
    id: 9,
    name: "Outcome + Learning",
    question: "What happened and what did we learn?",
    detail:
      "Post-breakpoint results are measured against target reduction and verified learning feeds the next signal.",
    stages: [10, 11],
    tone: "success",
  },
];

const CHAPTERS = [
  { title: "Problem Definition", hint: "Define the issue" },
  { title: "Containment", hint: "Protect customer or operation" },
  { title: "Root Cause", hint: "Evidence proves the cause" },
  { title: "Corrective Action", hint: "Action addresses cause" },
  { title: "Engineering / Process Change", hint: "Change, validation and release" },
  { title: "Implementation Breakpoint", hint: "Corrected condition begins" },
  { title: "PFMEA / Control Plan", hint: "Risk and controls updated" },
  { title: "Effectiveness", hint: "Did the fix work?" },
];

const DOMAINS = [
  { label: "Warranty / Field", tone: "primary" },
  { label: "Quality", tone: "primary" },
  { label: "Engineering", tone: "primary" },
  { label: "Manufacturing", tone: "primary" },
  { label: "Supplier", tone: "primary" },
  { label: "Product / Risk", tone: "primary" },
  { label: "Financial", tone: "warning" },
] as const;

const toneRing: Record<Loop["tone"], string> = {
  primary: "border-primary bg-primary/12",
  accent: "border-primary bg-primary/25",
  warning: "border-warning bg-warning/15",
  success: "border-success bg-success/15",
};

const toneDot: Record<Loop["tone"], string> = {
  primary: "bg-primary/70 text-primary-foreground",
  accent: "bg-primary text-primary-foreground",
  warning: "bg-warning text-warning-foreground",
  success: "bg-success text-success-foreground",
};

function StageCard({
  stage,
  active,
  onSelect,
  stacked = false,
  className,
}: {
  stage: Loop;
  active: boolean;
  onSelect: () => void;
  stacked?: boolean;
  className?: string;
}) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full min-w-0 rounded-lg border p-3 text-left transition-colors",
        stacked ? "flex flex-col gap-2" : "flex items-start gap-3",
        active
          ? toneRing[stage.tone]
          : "border-border bg-elevated/50 hover:border-primary/60 hover:bg-elevated",
        className,
      )}
    >
      <span
        className={cn(
          "num grid size-7 shrink-0 place-items-center rounded-full text-xs font-semibold",
          toneDot[stage.tone],
        )}
      >
        {stage.id}
      </span>
      <span className="min-w-0">
        <span className="block text-[0.82rem] font-semibold uppercase leading-tight">
          {stage.name}
        </span>
        <span className="mt-1 block text-xs text-muted-foreground">{stage.question}</span>
      </span>
    </button>
  );
}

function ClosedLoop() {
  const { data, activeProgramId } = useWarranty();
  const records = recordsForProgram(data, activeProgramId);
  const [selected, setSelected] = useState<number>(4);
  const stage = LOOP.find((s) => s.id === selected) ?? LOOP[0]!;
  const linked = records.filter((r) => stage.stages.includes(currentStage(r.stages).index));

  const left = LOOP.filter((s) => s.id <= 3);
  const record = LOOP.find((s) => s.id === 4)!;
  const bottom = LOOP.filter((s) => s.id >= 5);

  return (
    <AppShell
      title="Broadview Closed-Loop Application"
      subtitle="One governed Problem Record connects the complete story"
    >
      <div className="space-y-4">
        <Panel>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
            {DOMAINS.map((d) => (
              <span key={d.label} className="flex items-center gap-2.5">
                <span
                  className={cn(
                    "size-3.5 rounded-full",
                    d.tone === "warning" ? "bg-warning" : "bg-primary",
                  )}
                />
                <span className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-foreground/85">
                  {d.label}
                </span>
              </span>
            ))}
          </div>
        </Panel>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="space-y-4">
            <div className="grid gap-3 lg:grid-cols-[minmax(200px,300px)_minmax(0,1fr)]">
              <div className="space-y-3">
                {left.map((s) => (
                  <StageCard
                    key={s.id}
                    stage={s}
                    active={selected === s.id}
                    onSelect={() => setSelected(s.id)}
                    className="lg:min-h-[92px]"
                  />
                ))}
                <div className="rounded-lg border border-warning/70 bg-warning/10 p-3">
                  <div className="flex items-start gap-3">
                    <span className="size-6 shrink-0 rounded-full bg-warning" />
                    <div>
                      <p className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-warning">
                        Safety / Recall
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Governed escalation at any point
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={cn(
                  "rounded-lg border",
                  selected === 4 ? "border-warning" : "border-warning/50",
                )}
              >
                <button
                  onClick={() => setSelected(4)}
                  className="flex w-full items-center gap-4 rounded-t-lg bg-warning px-4 py-3 text-left text-warning-foreground"
                >
                  <span className="num text-lg font-semibold">4</span>
                  <span>
                    <span className="block text-[0.68rem] font-semibold uppercase tracking-[0.16em] opacity-80">
                      Broadview Problem Record
                    </span>
                    <span className="block text-sm font-semibold uppercase tracking-wide">
                      The governed digital thread
                    </span>
                  </span>
                </button>
                <div className="grid gap-2.5 p-3 sm:grid-cols-2">
                  {CHAPTERS.map((c) => (
                    <div
                      key={c.title}
                      className="rounded border border-primary/35 bg-elevated/60 px-3 py-2.5 text-center"
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide">{c.title}</p>
                      <p className="mt-1 text-[0.68rem] text-muted-foreground">{c.hint}</p>
                    </div>
                  ))}
                </div>
                <p className="num border-t border-border px-4 py-2 text-[0.7rem] text-muted-foreground">
                  {records.length} governed records in this program
                </p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              {bottom.map((s) => (
                <StageCard
                  key={s.id}
                  stage={s}
                  active={selected === s.id}
                  onSelect={() => setSelected(s.id)}
                  stacked
                />
              ))}
            </div>

            <div className="flex items-center gap-3 px-1">
              <span className="h-px flex-1 bg-primary/50" />
              <span className="text-[0.66rem] font-semibold uppercase tracking-[0.16em] text-primary">
                Verified learning feeds future signals
              </span>
              <span className="h-px flex-1 bg-primary/50" />
            </div>

            <div className="rounded-lg bg-warning px-4 py-3.5 text-center">
              <p className="text-sm font-semibold uppercase tracking-wide text-warning-foreground">
                One problem record. One connected story. From signal to action to verified outcome.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Panel title={`Stage ${stage.id}`} subtitle={stage.name}>
              <p className="text-sm leading-relaxed text-foreground/90">{stage.detail}</p>
              <p className="mt-3 text-xs text-muted-foreground">
                Maps to operational workflow stage{stage.stages.length > 1 ? "s" : ""}{" "}
                <span className="num text-primary">{stage.stages.join(", ")}</span> of 11.
              </p>
            </Panel>

            <Panel
              title="Records currently at this stage"
              subtitle={`${linked.length} of ${records.length} records`}
            >
              {linked.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No records are sitting at this stage right now.
                </p>
              ) : (
                <ul className="space-y-2">
                  {linked.map((r) => (
                    <li key={r.id}>
                      <Link
                        to="/demo/problem-record/$id"
                        params={{ id: r.id }}
                        className="block rounded border border-border/70 bg-elevated/40 p-3 transition-colors hover:border-primary/60"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="num text-xs text-primary">{r.code}</span>
                          <RiskBadge risk={r.risk} score={r.riskScore} />
                          <StatusChip status={r.status} />
                        </div>
                        <p className="mt-1.5 text-sm">{r.title}</p>
                        <p className="num mt-1 text-xs text-muted-foreground">
                          {fmtCompactCurrency(r.financial.exposure)} exposure · {r.owner}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
